/**
 * AI Job Posting Generator Modal Component
 * 
 * A comprehensive modal that guides users through providing a job brief
 * for AI to generate a complete job posting.
 * 
 * Features:
 * - Form validation with helpful error messages
 * - Loading states with animations
 * - Credit checking and usage tracking
 * - Rate limiting protection
 * - Responsive design
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateJobPosting, checkAICredits, isGenerationAllowed, deductAICredits } from '../../../utils/aiService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

export const AIGeneratorModal = React.memo(({
  isOpen,
  onClose,
  onGenerate,
  userProfile
}) => {
  const navigate = useNavigate();
  
  // Form state
  const [briefData, setBriefData] = useState({
    title: '',
    experienceLevel: 'intermediate',
    jobType: 'contract',
    location: '',
    isRemote: false,
    skills: [],
    companyContext: '',
    specialRequirements: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [step, setStep] = useState(1); // 1: Form, 2: Review, 3: Generating
  const [skillInput, setSkillInput] = useState('');

  // Credit state
  const [creditsInfo, setCreditsInfo] = useState({
    creditsRemaining: 0,
    hasCredits: false,
    checked: false
  });

  // Check credits on modal open
  useEffect(() => {
    if (isOpen && userProfile?.id && !creditsInfo.checked) {
      checkCreditsAndRateLimit();
    }
  }, [isOpen, userProfile?.id]);

  /**
   * Check credits and rate limits
   */
  const checkCreditsAndRateLimit = async () => {
    try {
      // Check credits
      const creditResult = await checkAICredits(userProfile.id);
      setCreditsInfo({
        ...creditResult,
        checked: true
      });

      // Check rate limit
      const rateLimitResult = await isGenerationAllowed(userProfile.id);
      if (!rateLimitResult.allowed) {
        setError(
          `Rate limit reached. You can generate ${rateLimitResult.maxPerHour} job postings per hour. ` +
          `Try again after: ${new Date(rateLimitResult.timeUntilReset).toLocaleTimeString()}`
        );
      }
    } catch (err) {
      console.error('Failed to check credits:', err);
      // Don't block on this error
    }
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setBriefData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  /**
   * Add skill to list
   */
  const addSkill = useCallback(() => {
    const trimmed = skillInput.trim();
    if (trimmed && !briefData.skills.includes(trimmed)) {
      setBriefData(prev => ({
        ...prev,
        skills: [...prev.skills, trimmed]
      }));
      setSkillInput('');
    }
  }, [skillInput, briefData.skills]);

  /**
   * Remove skill from list
   */
  const removeSkill = useCallback((skillToRemove) => {
    setBriefData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skillToRemove)
    }));
  }, []);

  /**
   * Validate form before generation
   */
  const validateForm = () => {
    if (!briefData.title.trim()) {
      setError('Please enter a job title');
      return false;
    }
    if (!briefData.location.trim()) {
      setError('Please enter a location or "Remote"');
      return false;
    }
    if (briefData.skills.length === 0) {
      setError('Please add at least one skill');
      return false;
    }
    return true;
  };

  /**
   * Handle AI generation
   */
  const handleGenerateClick = async () => {
    setError(null);

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check credits
    if (!creditsInfo.hasCredits) {
      setError('Insufficient AI credits. Please upgrade your plan.');
      return;
    }

    setStep(3);
    setLoading(true);

    try {
      // Call AI service
      const result = await generateJobPosting(briefData);

      if (!result.success) {
        setError(result.error || 'Failed to generate job posting');
        setStep(1);
        setLoading(false);
        return;
      }

      // Deduct AI credit on successful generation
      const deductResult = await deductAICredits(userProfile.id, 1);
      if (!deductResult.success) {
        console.error('[AIGeneratorModal] Failed to deduct credits:', deductResult.error);
        // Don't block the user if credit deduction fails - they still got the generation
      }

      // Success - pass data to parent with location from briefData
      const generatedDataWithLocation = {
        ...result.data,
        location: briefData.location,
        isRemote: briefData.isRemote
      };
      onGenerate(generatedDataWithLocation);
      
      // Show success message
      setSuccessMessage('✨ Job posting generated successfully!');
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
        reset();
      }, 1500);
    } catch (err) {
      setError(`Generation failed: ${err.message}`);
      setStep(1);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset form
   */
  const reset = () => {
    setBriefData({
      title: '',
      experienceLevel: 'intermediate',
      jobType: 'contract',
      location: '',
      isRemote: false,
      skills: [],
      companyContext: '',
      specialRequirements: ''
    });
    setSkillInput('');
    setError(null);
    setSuccessMessage('');
    setStep(1);
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-3xl">✨</span>
            <div>
              <h2 className="text-2xl font-bold">Job Posts Generator</h2>
              <p className="text-blue-100 text-sm mt-1">Create professional job posts in seconds</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Step Indicator */}
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((stepNum) => (
              <div key={stepNum} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= stepNum
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {stepNum}
                </div>
                {stepNum < 3 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      step > stepNum ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Step Labels */}
          <div className="flex justify-between text-xs text-text-muted px-2">
            <span>Job Brief</span>
            <span>Review</span>
            <span>Generating</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-red-800">Error</h3>
                  <p className="text-red-700 text-sm mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <p className="text-green-700 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Credits Info */}
          {creditsInfo.checked && (
            <div className={`p-3 rounded-lg flex items-center justify-between ${
              creditsInfo.hasCredits
                ? 'bg-blue-50 border border-blue-200'
                : 'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">⚡</span>
                <div className="text-sm">
                  <p className="font-medium text-text-primary">
                    Credits: {creditsInfo.creditsRemaining >= 999999 ? 'Unlimited' : creditsInfo.creditsRemaining}
                  </p>
                  <p className="text-text-secondary text-xs">
                    {creditsInfo.creditsRemaining >= 999999 ? 'Unlimited AI generations' : '1 generation = 1 credit'}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => navigate('/pricing')}
                className={`text-xs px-3 py-1 rounded transition-opacity cursor-pointer ${
                  creditsInfo.hasCredits
                    ? 'text-white hover:opacity-90'
                    : 'bg-yellow-600 text-white hover:bg-yellow-700'
                }`}
                style={creditsInfo.hasCredits ? { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' } : {}}
                title="Purchase more credits for AI generation"
              >
                {creditsInfo.hasCredits ? '➕ Buy More' : 'Upgrade'}
              </button>
            </div>
          )}

          {/* Generating State */}
          {step === 3 && loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin" />
              <p className="text-lg font-medium text-text-primary">Generating your job posting...</p>
              <p className="text-sm text-text-secondary">This usually takes 10-30 seconds</p>
            </div>
          ) : (
            <>
              {/* Form Step */}
              <div className={step === 1 ? 'block' : 'hidden space-y-4'}>
                {/* Job Title */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Job Title <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="title"
                    value={briefData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Social Media Manager, Content Writer, Customer Support"
                    disabled={loading}
                  />
                  <p className="text-xs text-text-muted mt-1">Be specific - this helps AI generate better content</p>
                </div>

                {/* Experience & Job Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Experience Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="experienceLevel"
                      value={briefData.experienceLevel}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:ring-2 focus:ring-primary"
                    >
                      <option value="entry">Entry Level (0-2 years)</option>
                      <option value="intermediate">Intermediate (2-5 years)</option>
                      <option value="senior">Senior (5+ years)</option>
                      <option value="expert">Expert (10+ years)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Employment Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="jobType"
                      value={briefData.jobType}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:ring-2 focus:ring-primary"
                    >
                      <option value="full_time">Full-Time</option>
                      <option value="part_time">Part-Time</option>
                      <option value="contract">Contract</option>
                      <option value="freelance">Freelance</option>
                    </select>
                  </div>
                </div>

                {/* Location & Remote */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="location"
                      value={briefData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., New York, Remote, EST timezone"
                      disabled={loading}
                    />
                  </div>

                  <div className="flex items-end pb-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="isRemote"
                        checked={briefData.isRemote}
                        onChange={handleInputChange}
                        disabled={loading}
                        className="w-4 h-4 rounded border-border"
                      />
                      <span className="text-sm font-medium text-text-primary">Remote Only</span>
                    </label>
                  </div>
                </div>

                {/* Skills */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Key Skills <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      type="text"
                      value={skillInput}
                      onChange={(e) => setSkillInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                      placeholder="Add a skill and press Enter"
                      disabled={loading}
                    />
                    <Button
                      variant="outline"
                      onClick={addSkill}
                      disabled={loading || !skillInput.trim()}
                    >
                      Add
                    </Button>
                  </div>

                  {/* Skills Tags */}
                  <div className="flex flex-wrap gap-2">
                    {briefData.skills.map((skill) => (
                      <div
                        key={skill}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                      >
                        {skill}
                        <button
                          onClick={() => removeSkill(skill)}
                          disabled={loading}
                          className="hover:text-blue-900 disabled:opacity-50"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-text-muted mt-2">
                    {briefData.skills.length} skill{briefData.skills.length !== 1 ? 's' : ''} added
                  </p>
                </div>

                {/* Company Context */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Company Context (Optional)
                  </label>
                  <textarea
                    name="companyContext"
                    value={briefData.companyContext}
                    onChange={handleInputChange}
                    placeholder="Tell us about your company culture, mission, and what makes it special..."
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:ring-2 focus:ring-primary h-24 resize-none"
                    disabled={loading}
                  />
                  <p className="text-xs text-text-muted mt-1">This helps AI match your company's tone</p>
                </div>

                {/* Special Requirements */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Special Requirements (Optional)
                  </label>
                  <textarea
                    name="specialRequirements"
                    value={briefData.specialRequirements}
                    onChange={handleInputChange}
                    placeholder="Any specific requirements? e.g., 'Must speak Spanish', 'Experience with Shopify required'"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-text focus:ring-2 focus:ring-primary h-20 resize-none"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Review Step */}
              {step >= 2 && (
                <div className="space-y-3 bg-secondary-50 rounded-lg p-4">
                  <h3 className="font-semibold text-text-primary">Review Your Brief:</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-text-muted">Title:</span>
                      <p className="font-medium text-text-primary">{briefData.title}</p>
                    </div>
                    <div>
                      <span className="text-text-muted">Level:</span>
                      <p className="font-medium text-text-primary capitalize">{briefData.experienceLevel}</p>
                    </div>
                    <div>
                      <span className="text-text-muted">Type:</span>
                      <p className="font-medium text-text-primary capitalize">{briefData.jobType}</p>
                    </div>
                    <div>
                      <span className="text-text-muted">Location:</span>
                      <p className="font-medium text-text-primary">{briefData.location}</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-secondary-50 border-t border-border p-6 flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          {step === 1 && (
            <Button
              variant="primary"
              onClick={() => {
                if (validateForm()) {
                  setStep(2);
                }
              }}
              disabled={loading}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                disabled={loading}
              >
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handleGenerateClick}
                disabled={loading || !creditsInfo.hasCredits}
              >
                {loading ? 'Generating...' : '✨ Generate'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

AIGeneratorModal.displayName = 'AIGeneratorModal';

export default AIGeneratorModal;
